import React, { useState, useEffect } from 'react';
import { siteContentAPI, portfolioItemsAPI, filmsAPI, uploadAPI } from './services/htmlSiteAPI';
import { X, Upload, Save, Trash2, Plus, Film, Image as ImageIcon, User, ArrowLeft, LogOut } from 'lucide-react';
import AdminLogin from './AdminLogin';
import './styles/admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HTMLSiteAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Data state
  const [siteContent, setSiteContent] = useState(null);
  const [films, setFilms] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  
  // Form state
  const [profileForm, setProfileForm] = useState({});
  const [filmForm, setFilmForm] = useState({
    title: '', year: new Date().getFullYear(), location: '', type: 'Short Film',
    description: '', awards: '', role: '', videoUrl: '', thumbnail: '', order: 0
  });
  const [galleryForm, setGalleryForm] = useState({
    caption: '', filmId: '', order: 0, imageUrl: '', category: 'photo', description: '', year: '', location: '', type: 'photo', videoUrl: ''
  });
  const [editingFilm, setEditingFilm] = useState(null);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      setCheckingAuth(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      setIsAuthenticated(data.valid);
    } catch (err) {
      console.error('Auth check error:', err);
      setIsAuthenticated(false);
    }
    setCheckingAuth(false);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      try {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
      } catch (err) {
        console.error('Logout error:', err);
      }
    }
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, filmsRes, portfolioRes] = await Promise.all([
        siteContentAPI.get(),
        filmsAPI.getAll(),
        portfolioItemsAPI.getAll()
      ]);
      setSiteContent(contentRes.data);
      setProfileForm(contentRes.data);
      setFilms(filmsRes.data);
      setPortfolio(portfolioRes.data);
    } catch (err) {
      showMessage('error', 'Failed to load data');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await siteContentAPI.update(profileForm);
      showMessage('success', 'Site content updated successfully!');
      await fetchData();
    } catch (err) {
      showMessage('error', 'Failed to update content');
    }
    setLoading(false);
  };

  const handleFilmSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const filmData = {
        ...filmForm,
        year: parseInt(filmForm.year),
        order: parseInt(filmForm.order),
        awards: filmForm.awards ? filmForm.awards.split(',').map(a => a.trim()) : []
      };
      
      if (editingFilm) {
        await filmsAPI.update(editingFilm._id, filmData);
        showMessage('success', 'Film updated successfully!');
      } else {
        await filmsAPI.create(filmData);
        showMessage('success', 'Film added successfully!');
      }
      
      setFilmForm({
        title: '', year: new Date().getFullYear(), location: '', type: 'Short Film',
        description: '', awards: '', role: '', videoUrl: '', thumbnail: '', order: 0
      });
      setEditingFilm(null);
      await fetchData();
    } catch (err) {
      showMessage('error', 'Failed to save film');
    }
    setLoading(false);
  };

  const handleFilmDelete = async (filmId) => {
    if (!window.confirm('Are you sure you want to delete this film?')) return;
    setLoading(true);
    try {
      await filmsAPI.delete(filmId);
      showMessage('success', 'Film deleted successfully!');
      await fetchData();
    } catch (err) {
      showMessage('error', 'Failed to delete film');
    }
    setLoading(false);
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log('Uploading file:', file.name, 'Type:', file.type, 'Size:', file.size);
    
    setUploadingImage(type);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.url;
      
      console.log('Upload successful, URL:', imageUrl);
      
      if (type === 'profile-hero') {
        setProfileForm({ ...profileForm, heroPosterUrl: imageUrl });
      } else if (type === 'profile-portrait') {
        setProfileForm({ ...profileForm, portraitImage: imageUrl });
      } else if (type === 'film-thumbnail') {
        setFilmForm({ ...filmForm, thumbnail: imageUrl });
      } else if (type === 'gallery') {
        setGalleryForm({ ...galleryForm, imageUrl });
      }
      
      showMessage('success', 'Image uploaded successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      showMessage('error', 'Failed to upload image: ' + (err.response?.data?.detail || err.message));
    }
    setUploadingImage(null);
  };

  const handleGallerySubmit = async (e) => {
    e.preventDefault();
    const isVideo = galleryForm.type === 'video';
    
    // Validate: need thumbnail image, and video URL if it's a video
    if (!galleryForm.imageUrl && !editingPortfolio?.thumbnail) {
      showMessage('error', 'Please upload a thumbnail image');
      return;
    }
    if (isVideo && !galleryForm.videoUrl && !editingPortfolio?.media) {
      showMessage('error', 'Please enter a video URL');
      return;
    }
    
    setLoading(true);
    try {
      const thumbnailUrl = galleryForm.imageUrl || editingPortfolio?.thumbnail;
      const mediaUrl = isVideo 
        ? (galleryForm.videoUrl || editingPortfolio?.media)
        : thumbnailUrl;
      
      const portfolioData = {
        title: galleryForm.caption,
        category: galleryForm.category || 'photo',
        type: galleryForm.type || 'photo',
        thumbnail: thumbnailUrl,
        media: mediaUrl,
        description: galleryForm.description || galleryForm.caption,
        year: galleryForm.year || new Date().getFullYear().toString(),
        location: galleryForm.location || '',
        role: '',
        awards: '',
        order: parseInt(galleryForm.order)
      };
      
      if (editingPortfolio) {
        await portfolioItemsAPI.update(editingPortfolio._id, portfolioData);
        showMessage('success', 'Portfolio item updated successfully!');
        setEditingPortfolio(null);
      } else {
        await portfolioItemsAPI.create(portfolioData);
        showMessage('success', 'Portfolio item added successfully!');
      }
      setGalleryForm({ caption: '', filmId: '', order: 0, imageUrl: '', category: 'photo', description: '', year: '', location: '', type: 'photo', videoUrl: '' });
      await fetchData();
    } catch (err) {
      showMessage('error', editingPortfolio ? 'Failed to update portfolio item' : 'Failed to add portfolio item');
      console.error('Portfolio submit error:', err);
    }
    setLoading(false);
  };

  const handleGalleryDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this portfolio item?')) return;
    setLoading(true);
    try {
      await portfolioItemsAPI.delete(itemId);
      showMessage('success', 'Portfolio item deleted successfully!');
      await fetchData();
    } catch (err) {
      showMessage('error', 'Failed to delete portfolio item');
    }
    setLoading(false);
  };

  if (!siteContent) {
    return <div className="admin-loading">Loading admin panel...</div>;
  }

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-box">
          <p style={{ textAlign: 'center', color: '#999' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <h1 className="admin-title">Website Admin Panel</h1>
          <div className="admin-header-actions">
            <a href="/site.html" className="admin-back-btn" target="_blank">
              <ArrowLeft size={20} />
              View Site
            </a>
            <button onClick={handleLogout} className="admin-logout-btn">
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Message */}
      {message.text && (
        <div className={`admin-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User size={18} />
          Profile
        </button>
        <button 
          className={`admin-tab ${activeTab === 'films' ? 'active' : ''}`}
          onClick={() => setActiveTab('films')}
        >
          <Film size={18} />
          Films
        </button>
        <button 
          className={`admin-tab ${activeTab === 'gallery' ? 'active' : ''}`}
          onClick={() => setActiveTab('gallery')}
        >
          <ImageIcon size={18} />
          Portfolio
        </button>
      </div>

      {/* Content */}
      <div className="admin-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form className="admin-form" onSubmit={handleProfileUpdate}>
            <h2 className="admin-section-title">Site Content</h2>
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={profileForm.name || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={profileForm.title || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, title: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="admin-form-group">
              <label>About Text (HTML allowed)</label>
              <textarea
                value={profileForm.aboutText || ''}
                onChange={(e) => setProfileForm({ ...profileForm, aboutText: e.target.value })}
                rows={10}
                placeholder="Full about section with paragraphs. HTML tags like <p>, <em> are allowed."
              />
              <small style={{color: '#999', fontSize: '0.875rem'}}>Tip: Use &lt;p&gt; for paragraphs, &lt;em&gt; for italics</small>
            </div>

            <div className="admin-form-group">
              <label>Portrait Image</label>
              <div className="admin-upload-area">
                {profileForm.portraitImage && (
                  <img src={profileForm.portraitImage} alt="Portrait" className="admin-preview-img" />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'profile-portrait')}
                  id="portrait-upload"
                  className="admin-file-input"
                />
                <label htmlFor="portrait-upload" className="admin-upload-btn">
                  <Upload size={18} />
                  {uploadingImage === 'profile-portrait' ? 'Uploading...' : 'Upload Portrait'}
                </label>
              </div>
            </div>

            <div className="admin-form-group">
              <label>Hero Video URL</label>
              <input
                type="url"
                value={profileForm.heroVideoUrl || ''}
                onChange={(e) => setProfileForm({ ...profileForm, heroVideoUrl: e.target.value })}
                placeholder="https://cdn.coverr.co/videos/..."
              />
            </div>

            <div className="admin-form-group">
              <label>Director's Statement (HTML allowed)</label>
              <textarea
                value={profileForm.directorStatement || ''}
                onChange={(e) => setProfileForm({ ...profileForm, directorStatement: e.target.value })}
                rows={8}
                placeholder="Your director's statement. HTML tags like <p>, <em> are allowed."
              />
              <small style={{color: '#999', fontSize: '0.875rem'}}>Tip: Use &lt;p&gt; for paragraphs, &lt;em&gt; for italics</small>
            </div>

            <div className="admin-form-group">
              <label>Contact Email</label>
              <input
                type="email"
                value={profileForm.contactEmail || ''}
                onChange={(e) => setProfileForm({ ...profileForm, contactEmail: e.target.value })}
                required
              />
            </div>

            <button type="submit" className="admin-submit-btn" disabled={loading}>
              <Save size={18} />
              {loading ? 'Saving...' : 'Save Content'}
            </button>
          </form>
        )}

        {/* Films Tab */}
        {activeTab === 'films' && (
          <div className="admin-films-container">
            <div className="admin-films-list">
              <h2 className="admin-section-title">Your Films</h2>
              {films.map(film => (
                <div key={film._id} className="admin-film-card">
                  {film.thumbnail && (
                    <img src={film.thumbnail} alt={film.title} className="admin-film-thumb" />
                  )}
                  <div className="admin-film-info">
                    <h3>{film.title}</h3>
                    <p>{film.year} • {film.location}</p>
                    <p className="admin-film-desc">{film.description.substring(0, 100)}...</p>
                  </div>
                  <div className="admin-film-actions">
                    <button
                      className="admin-btn-edit"
                      onClick={() => {
                        setEditingFilm(film);
                        setFilmForm({
                          ...film,
                          awards: film.awards.join(', ')
                        });
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="admin-btn-delete"
                      onClick={() => handleFilmDelete(film._id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <form className="admin-form admin-film-form" onSubmit={handleFilmSubmit}>
              <h2 className="admin-section-title">
                {editingFilm ? 'Edit Film' : 'Add New Film'}
              </h2>

              <div className="admin-form-group">
                <label>Film Title *</label>
                <input
                  type="text"
                  value={filmForm.title}
                  onChange={(e) => setFilmForm({ ...filmForm, title: e.target.value })}
                  required
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Year *</label>
                  <input
                    type="number"
                    value={filmForm.year}
                    onChange={(e) => setFilmForm({ ...filmForm, year: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Location *</label>
                  <input
                    type="text"
                    value={filmForm.location}
                    onChange={(e) => setFilmForm({ ...filmForm, location: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Type *</label>
                  <select
                    value={filmForm.type}
                    onChange={(e) => setFilmForm({ ...filmForm, type: e.target.value })}
                  >
                    <option>Short Film</option>
                    <option>Feature Film</option>
                    <option>Documentary</option>
                    <option>Music Video</option>
                    <option>Commercial</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-group">
                <label>Description *</label>
                <textarea
                  value={filmForm.description}
                  onChange={(e) => setFilmForm({ ...filmForm, description: e.target.value })}
                  rows={4}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Your Role</label>
                <input
                  type="text"
                  value={filmForm.role}
                  onChange={(e) => setFilmForm({ ...filmForm, role: e.target.value })}
                  placeholder="e.g., Director, Writer"
                />
              </div>

              <div className="admin-form-group">
                <label>Awards (comma-separated)</label>
                <input
                  type="text"
                  value={filmForm.awards}
                  onChange={(e) => setFilmForm({ ...filmForm, awards: e.target.value })}
                  placeholder="Best Film, Best Director, ..."
                />
              </div>

              <div className="admin-form-group">
                <label>Video URL (YouTube/Vimeo Embed) *</label>
                <input
                  type="url"
                  value={filmForm.videoUrl}
                  onChange={(e) => setFilmForm({ ...filmForm, videoUrl: e.target.value })}
                  placeholder="https://www.youtube.com/embed/..."
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Thumbnail Image *</label>
                <div className="admin-upload-area">
                  {filmForm.thumbnail && (
                    <img src={filmForm.thumbnail} alt="Thumbnail" className="admin-preview-img" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'film-thumbnail')}
                    id="film-thumb-upload"
                    className="admin-file-input"
                  />
                  <label htmlFor="film-thumb-upload" className="admin-upload-btn">
                    <Upload size={18} />
                    {uploadingImage === 'film-thumbnail' ? 'Uploading...' : 'Upload Thumbnail'}
                  </label>
                </div>
              </div>

              <div className="admin-form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={filmForm.order}
                  onChange={(e) => setFilmForm({ ...filmForm, order: e.target.value })}
                  min="0"
                />
              </div>

              <div className="admin-form-actions">
                {editingFilm && (
                  <button
                    type="button"
                    className="admin-cancel-btn"
                    onClick={() => {
                      setEditingFilm(null);
                      setFilmForm({
                        title: '', year: new Date().getFullYear(), location: '', type: 'Short Film',
                        description: '', awards: '', role: '', videoUrl: '', thumbnail: '', order: 0
                      });
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button type="submit" className="admin-submit-btn" disabled={loading}>
                  <Save size={18} />
                  {loading ? 'Saving...' : editingFilm ? 'Update Film' : 'Add Film'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="admin-gallery-container">
            <div className="admin-gallery-grid">
              {portfolio.map(item => (
                <div key={item._id} className="admin-gallery-card">
                  <img src={item.thumbnail} alt={item.title} />
                  <div className="admin-gallery-overlay">
                    <p>{item.title}</p>
                    <div className="admin-gallery-actions">
                      <button
                        className="admin-btn-edit-gallery"
                        onClick={() => {
                          setEditingPortfolio(item);
                          setGalleryForm({
                            caption: item.title || '',
                            category: item.category || 'photo',
                            imageUrl: item.thumbnail || '',
                            description: item.description || '',
                            year: item.year || '',
                            location: item.location || '',
                            order: item.order || 0,
                            filmId: '',
                            type: item.type || 'photo',
                            videoUrl: item.media || ''
                          });
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn-delete-gallery"
                        onClick={() => handleGalleryDelete(item._id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <form className="admin-form admin-gallery-form" onSubmit={handleGallerySubmit}>
              <h2 className="admin-section-title">
                {editingPortfolio ? 'Edit Portfolio Item' : 'Add Portfolio Item'}
              </h2>

              <div className="admin-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={galleryForm.caption}
                  onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })}
                  placeholder="e.g., Infinity Pool - Behind the Scenes"
                  required
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Type *</label>
                  <select
                    value={galleryForm.type || 'photo'}
                    onChange={(e) => setGalleryForm({ ...galleryForm, type: e.target.value })}
                  >
                    <option value="photo">Photo</option>
                    <option value="video">Video</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label>Category *</label>
                  <select
                    value={galleryForm.category || 'photo'}
                    onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                  >
                    <option value="photo">Photo</option>
                    <option value="film">Film</option>
                    <option value="bts">Behind the Scenes</option>
                  </select>
                </div>
              </div>

              <div className="admin-form-group">
                <label>{galleryForm.type === 'video' ? 'Thumbnail Image *' : 'Image *'}</label>
                <div className="admin-upload-area">
                  {galleryForm.imageUrl && (
                    <img src={galleryForm.imageUrl} alt="Preview" className="admin-preview-img" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'gallery')}
                    id="gallery-upload"
                    className="admin-file-input"
                  />
                  <label htmlFor="gallery-upload" className="admin-upload-btn">
                    <Upload size={18} />
                    {uploadingImage === 'gallery' ? 'Uploading...' : 'Upload Image'}
                  </label>
                </div>
                {galleryForm.type === 'video' && (
                  <small style={{color: '#999', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block'}}>
                    Upload a thumbnail image that will be shown before the video plays
                  </small>
                )}
              </div>

              {galleryForm.type === 'video' && (
                <div className="admin-form-group">
                  <label>Video URL *</label>
                  <input
                    type="url"
                    value={galleryForm.videoUrl || ''}
                    onChange={(e) => setGalleryForm({ ...galleryForm, videoUrl: e.target.value })}
                    placeholder="https://vimeo.com/123456789 or Google Drive link"
                  />
                  <small style={{color: '#999', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block'}}>
                    Supported: Vimeo, YouTube embed, Google Drive preview links
                  </small>
                </div>
              )}

              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  value={galleryForm.description || ''}
                  onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                  rows={3}
                  placeholder="Brief description of this portfolio item"
                />
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label>Year</label>
                  <input
                    type="text"
                    value={galleryForm.year || ''}
                    onChange={(e) => setGalleryForm({ ...galleryForm, year: e.target.value })}
                    placeholder="2024"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={galleryForm.location || ''}
                    onChange={(e) => setGalleryForm({ ...galleryForm, location: e.target.value })}
                    placeholder="Croatia"
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={galleryForm.order}
                  onChange={(e) => setGalleryForm({ ...galleryForm, order: e.target.value })}
                  min="0"
                />
              </div>

              <div className="admin-form-actions">
                {editingPortfolio && (
                  <button
                    type="button"
                    className="admin-cancel-btn"
                    onClick={() => {
                      setEditingPortfolio(null);
                      setGalleryForm({ caption: '', filmId: '', order: 0, imageUrl: '', category: 'photo', description: '', year: '', location: '', type: 'photo', videoUrl: '' });
                    }}
                  >
                    Cancel
                  </button>
                )}
                <button type="submit" className="admin-submit-btn" disabled={loading}>
                  {editingPortfolio ? <Save size={18} /> : <Plus size={18} />}
                  {loading ? 'Saving...' : editingPortfolio ? 'Update Portfolio' : 'Add to Portfolio'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default HTMLSiteAdmin;